import React from 'react';
import { useTranslation } from 'react-i18next';

// Note: We can't use DS tooltip because:
// - we have links in some of the content
// - we don't want the tooltip to flip when reaching a boundary
import { Tooltip } from '@hotjar/webapp/components/Tooltip';

import styled from '@emotion/styled';
import type { EventMarker } from '../../types';
import { EventMarkerTooltipContent, getEventDescription } from './EventMarkerTooltipContent';
import { getTargetUrl } from '../../EventMarkerUtils';
import { EventMarkerAskTooltip } from './EventMarkerAskTooltip';

interface Props {
  eventMarker: EventMarker;
  isDesktopRecording: boolean;
  siteId: number;
  children: React.ReactElement;
  setIsHoveringMarker: (state: boolean) => void;
}

const WrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 328px;
`;

const ContentDiv = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EventMarkerTooltip = ({
  eventMarker,
  isDesktopRecording,
  siteId,
  children,
  setIsHoveringMarker,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  if (eventMarker.type === 'autotag_recording') {
    const targetUrl = getTargetUrl(siteId, eventMarker);

    if (
      (eventMarker.autotag_properties?.type === 'feedback' ||
        eventMarker.autotag_properties?.type === 'poll') &&
      eventMarker.autotag_properties.response_id !== null &&
      eventMarker.autotag_properties.response_id !== undefined
    ) {
      return (
        <Tooltip
          arrow={false}
          maxWidth={NaN}
          content={
            <EventMarkerAskTooltip
              type={eventMarker.autotag_properties.type}
              emotion={eventMarker.autotag_properties.e}
              widgetId={eventMarker.autotag_properties.id}
              responseId={eventMarker.autotag_properties.response_id}
              targetUrl={targetUrl}
              siteId={siteId}
              setIsHoveringMarker={setIsHoveringMarker}
            />
          }
          placement="top"
          interactive
          kind="unstyled"
        >
          {children}
        </Tooltip>
      );
    }

    // Return different tooltips with a link or not, depending on whether we have an id (consent).
    if (targetUrl) {
      return (
        <Tooltip
          content={
            <EventMarkerTooltipContent
              targetUrl={targetUrl}
              eventDescription={getEventDescription(eventMarker, isDesktopRecording, t)}
              setIsHoveringMarker={setIsHoveringMarker}
            />
          }
          placement="top"
          interactive
        >
          {children}
        </Tooltip>
      );
    }
  }

  return (
    <Tooltip
      content={
        <WrapperDiv>
          <ContentDiv>{getEventDescription(eventMarker, isDesktopRecording, t)}</ContentDiv>
        </WrapperDiv>
      }
      placement="top"
    >
      {children}
    </Tooltip>
  );
};
